import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Divider, Grid, Loader } from '@mantine/core';
import { AlertCircle } from 'tabler-icons-react';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import AppCard from '../../common/AppCard';
import { useMediaQueries, useModalState } from '../../../helpers/hooks';
import { PAYMENT_PROVIDER_LIST } from '../../../config/constants';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppFlexbox from '../../common/AppFlexbox';
import HeaderView from '../../common/HeaderView';
import AppImage from '../../common/AppImage';
import classes from '../../../styles/nestedStyles.module.css';
import FinancialPayeeOnboardingModal from './FinancialPayeeOnboardingModal';
import AssociationPayeeSettingsView from '../settings/AssociationPayeeSettingsView';

const FinancialPayees = ({ regAssociation }) => {
  const { state } = useContext(RegistrationAdminContext);
  const { state: modalState, onOpenModal, onCloseModal } = useModalState();
  const { isMobileOrSmaller } = useMediaQueries();
  const regAssociationData = state.regAssociations.value.find(
    (s) => s.pkRegAssociation === regAssociation?.pkRegAssociation
  );
  const loading = !regAssociation || state.regAssociations.loading;

  return loading ? (
    <AppCard style={{ flex: 1, padding: 0, height: '100%' }}>
      <Loader color="dark" size={40} style={{ margin: '80px auto' }} />
    </AppCard>
  ) : !regAssociationData ||
    !regAssociationData.regAssociationPaymentProvider ? (
    <AppCard
      style={{
        flex: 1,
        padding: 8,
        paddingBottom: 20,
        height: '100%',
        overflow: 'auto'
      }}
    >
      <HeaderView maxViewWidth={1100}>
        <AppStack>
          <AppCard radius="md" shadow="xs" style={{ padding: 0 }} withBorder>
            <AppFlexbox
              style={{
                padding: 8,
                gap: 8,
                backgroundColor: '#eca70a',
                color: '#fff'
              }}
            >
              <AppStack>
                <AlertCircle size={22} />
              </AppStack>
              <AppText style={{ fontSize: 14 }}>Attention required</AppText>
            </AppFlexbox>
            <AppStack style={{ padding: 8 }}>
              <AppText style={{ fontSize: 14 }}>
                A payment provider is required to complete onboarding and start
                accepting payments.
              </AppText>
            </AppStack>
          </AppCard>

          <Divider />

          <AppText style={{ fontSize: 20, fontWeight: 500 }}>
            Choose a Payment Provider
          </AppText>
          <AppStack style={{ gap: 0 }}>
            <AppText style={{ fontSize: 14 }}>
              Choose the payment provider that best fits your organization. Once
              connected, you’ll be able to securely accept payments and receive
              payouts. You can change your payment provider at any time.
            </AppText>
          </AppStack>
        </AppStack>

        <Grid>
          {PAYMENT_PROVIDER_LIST.map((p) => (
            <Grid.Col key={p.value} span={isMobileOrSmaller ? 12 : 6}>
              <AppCard
                className={classes['hover-action-card']}
                onClick={() => {
                  onOpenModal('ONBOARD', { fkPaymentProviderType: p.value });
                }}
                radius={8}
                style={{ padding: 0 }}
                withBorder
              >
                <AppStack style={{ gap: 8, padding: 16 }}>
                  <AppFlexbox style={{ gap: 16 }}>
                    <AppStack
                      style={{
                        maxWidth: 80,
                        maxHeight: 80,
                        width: '100%',
                        height: '100%',
                        borderRadius: 100,
                        overflow: 'hidden'
                      }}
                    >
                      <AppImage
                        src={p.image}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </AppStack>

                    <AppStack style={{ gap: 5 }}>
                      <AppStack style={{ gap: 0 }}>
                        <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                          {p.label}
                        </AppText>
                        <AppText style={{ fontSize: 14, color: '#666' }}>
                          {p.description}
                        </AppText>
                      </AppStack>
                      <AppText style={{ fontSize: 15, fontWeight: 500 }}>
                        {p.serviceFeeDescription}
                      </AppText>

                      {p.hasInstallments && (
                        <AppText
                          style={{
                            fontSize: 14,
                            color: '#666',
                            fontStyle: 'italic'
                          }}
                        >
                          {p.canCustomizeInstallments
                            ? 'Supports custom installment plans'
                            : 'Standard installment plan available'}
                        </AppText>
                      )}
                    </AppStack>
                  </AppFlexbox>
                </AppStack>
              </AppCard>
            </Grid.Col>
          ))}
        </Grid>
      </HeaderView>

      <FinancialPayeeOnboardingModal
        fkPaymentProviderType={modalState.item?.fkPaymentProviderType}
        isOpen={modalState.isOpen && modalState.action === 'ONBOARD'}
        onClose={onCloseModal}
        regAssociation={regAssociationData}
      />
    </AppCard>
  ) : (
    <AppCard style={{ flex: 1, padding: 0, height: '100%', overflow: 'auto' }}>
      <HeaderView maxViewWidth={800}>
        <AssociationPayeeSettingsView regAssociation={regAssociationData} />
      </HeaderView>
    </AppCard>
  );
};

FinancialPayees.propTypes = { regAssociation: PropTypes.object };

export default FinancialPayees;
